import React, { useState } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Grid,
  GridItem,
  Popover,
  PopoverTrigger as ChakraPopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  IconButton,
  HStack,
} from '@chakra-ui/react';

import { FaCalendarAlt, FaTimes } from 'react-icons/fa';
import { DayPicker } from 'react-day-picker';
import { format, isValid } from 'date-fns';
import './DateRangePicker.css';

const PopoverTrigger: React.FC<{ children: React.ReactNode }> = ChakraPopoverTrigger;

interface DateRangePickerProps {
  label: string;
  fromValue?: string; // DateTimeISO string
  toValue?: string; // DateTimeISO string
  onFromChange: (value: string | undefined) => void;
  onToChange: (value: string | undefined) => void;
  placeholder?: string;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  label,
  fromValue,
  toValue,
  onFromChange,
  onToChange,
}) => {
  const [fromPickerOpen, setFromPickerOpen] = useState(false);
  const [toPickerOpen, setToPickerOpen] = useState(false);

  const handleFromDateSelect = (date: Date | undefined) => {
    if (date) {
      // Convert to DateTimeISO string (start of day)
      const newDate = new Date(date);
      newDate.setHours(0, 0, 0, 0);
      onFromChange(newDate.toISOString());
    } else {
      onFromChange(undefined);
    }
    setFromPickerOpen(false);
  };

  const handleToDateSelect = (date: Date | undefined) => {
    if (date) {
      // Convert to DateTimeISO string (end of day)
      const newDate = new Date(date);
      newDate.setHours(23, 59, 59, 999);
      onToChange(newDate.toISOString());
    } else {
      onToChange(undefined);
    }
    setToPickerOpen(false);
  };

  const formatDisplayDate = (isoString?: string) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    if (!isValid(date)) return '';
    return format(date, 'MMM dd, yyyy');
  };

  const clearFromDate = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFromChange(undefined);
  };

  const clearToDate = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToChange(undefined);
  };

  return (
    <FormControl>
      <FormLabel fontSize="sm" fontWeight="500" color="gray.700" mb={2}>
        {label}
      </FormLabel>
      <Grid templateColumns="1fr 1fr" gap={3}>
        <GridItem>
          <Popover isOpen={fromPickerOpen} onClose={() => setFromPickerOpen(false)} placement="bottom-start">
            <PopoverTrigger>
              <Box position="relative">
                <Input
                  placeholder="From date"
                  value={formatDisplayDate(fromValue)}
                  readOnly
                  cursor="pointer"
                  onClick={() => setFromPickerOpen(!fromPickerOpen)}
                  bg="gray.50"
                  border="1px"
                  borderColor="gray.300"
                  _hover={{ borderColor: "gray.400" }}
                  _focus={{ 
                    borderColor: "blue.500", 
                    bg: "white",
                    boxShadow: "0 0 0 1px #3182ce"
                  }}
                  size="md"
                  fontSize="sm"
                  pr={fromValue ? "70px" : "40px"}
                />
                <HStack 
                  position="absolute" 
                  right={2} 
                  top="50%" 
                  transform="translateY(-50%)"
                  spacing={1}
                >
                  {fromValue && (
                    <IconButton
                      aria-label="Clear from date"
                      icon={<FaTimes />}
                      size="xs"
                      variant="ghost"
                      color="gray.500"
                      _hover={{ color: "gray.700" }}
                      onClick={clearFromDate}
                    />
                  )}
                  <Box color="gray.400" fontSize="sm">
                    <FaCalendarAlt />
                  </Box>
                </HStack>
              </Box>
            </PopoverTrigger>
            <PopoverContent width="auto" p={0}>
              <PopoverArrow />
              <PopoverBody p={4}>
                <DayPicker
                  mode="single"
                  selected={fromValue ? new Date(fromValue) : undefined}
                  onSelect={handleFromDateSelect}
                  showOutsideDays
                />
              </PopoverBody>
            </PopoverContent>
          </Popover>
        </GridItem>

        <GridItem>
          <Popover isOpen={toPickerOpen} onClose={() => setToPickerOpen(false)} placement="bottom-start">
            <PopoverTrigger>
              <Box position="relative">
                <Input
                  placeholder="Until date"
                  value={formatDisplayDate(toValue)}
                  readOnly
                  cursor="pointer"
                  onClick={() => setToPickerOpen(!toPickerOpen)}
                  bg="gray.50"
                  border="1px"
                  borderColor="gray.300"
                  _hover={{ borderColor: "gray.400" }}
                  _focus={{ 
                    borderColor: "blue.500", 
                    bg: "white",
                    boxShadow: "0 0 0 1px #3182ce"
                  }}
                  size="md"
                  fontSize="sm"
                  pr={toValue ? "70px" : "40px"}
                />
                <HStack 
                  position="absolute" 
                  right={2} 
                  top="50%" 
                  transform="translateY(-50%)"
                  spacing={1}
                >
                  {toValue && (
                    <IconButton
                      aria-label="Clear until date"
                      icon={<FaTimes />}
                      size="xs"
                      variant="ghost"
                      color="gray.500"
                      _hover={{ color: "gray.700" }}
                      onClick={clearToDate}
                    />
                  )}
                  <Box color="gray.400" fontSize="sm">
                    <FaCalendarAlt />
                  </Box>
                </HStack>
              </Box>
            </PopoverTrigger>
            <PopoverContent width="auto" p={0}>
              <PopoverArrow />
              <PopoverBody p={4}>
                <DayPicker
                  mode="single"
                  selected={toValue ? new Date(toValue) : undefined}
                  onSelect={handleToDateSelect}
                  showOutsideDays
                />
              </PopoverBody>
            </PopoverContent>
          </Popover>
        </GridItem>
      </Grid>
    </FormControl>
  );
};